import {
    ChatBubbleOutlineOutlined,
    ConfirmationNumber,
    DeleteOutlineOutlined,
    Download,
    MoreVert,
    Upload,
} from '@mui/icons-material';
import {
    CircularProgress,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
} from '@mui/material';
import { useRef, useState, type ChangeEvent, type MouseEvent } from 'react';

import { DOCUMENT_UPLOAD_ACCEPT } from './uploadDocument';

import useTranslate from 'hooks/useTranslate';

interface DocumentActionsMenuProps {
    hasFile: boolean;
    uploading?: boolean;
    onCreateTicket: () => void;
    onChat?: () => void;
    onUpload: (file: File) => void;
    onDownload: () => void;
    onDelete: () => void;
}

const DocumentActionsMenu = ({
    hasFile,
    uploading,
    onCreateTicket,
    onChat,
    onUpload,
    onDownload,
    onDelete,
}: DocumentActionsMenuProps) => {
    const { translate } = useTranslate();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const closeMenu = () => setAnchorEl(null);

    const handleOpen = (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleAction = (action: () => void) => {
        closeMenu();
        action();
    };

    const handleUploadChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) onUpload(file);
        event.target.value = '';
    };

    return (
        <>
            <input
                ref={inputRef}
                type="file"
                hidden
                accept={DOCUMENT_UPLOAD_ACCEPT}
                onChange={handleUploadChange}
            />
            <IconButton
                size="small"
                onClick={handleOpen}
                aria-label={translate('actions')}
                disabled={uploading}
            >
                {uploading ? <CircularProgress size={18} /> : <MoreVert fontSize="small" />}
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={closeMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { minWidth: 180, mt: 0.5 } } }}
            >
                <MenuItem onClick={() => handleAction(onCreateTicket)}>
                    <ListItemIcon>
                        <ConfirmationNumber fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{translate('createTicket')}</ListItemText>
                </MenuItem>
                <MenuItem disabled={!hasFile} onClick={() => onChat && handleAction(onChat)}>
                    <ListItemIcon>
                        <ChatBubbleOutlineOutlined fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{translate('documentChat')}</ListItemText>
                </MenuItem>
                <MenuItem
                    disabled={uploading}
                    onClick={() => {
                        closeMenu();
                        inputRef.current?.click();
                    }}
                >
                    <ListItemIcon>
                        <Upload fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{translate('documentsUpload')}</ListItemText>
                </MenuItem>
                <MenuItem disabled={!hasFile} onClick={() => handleAction(onDownload)}>
                    <ListItemIcon>
                        <Download fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{translate('documentsDownload')}</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleAction(onDelete)} sx={{ color: 'error.main' }}>
                    <ListItemIcon>
                        <DeleteOutlineOutlined fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText>{translate('deleteDocument')}</ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
};

export default DocumentActionsMenu;
